import type { components } from "@/api/schema";

export type RevenueRow = components["schemas"]["RevenueReportRow"];
export type RevenueCurrencyTotals = components["schemas"]["RevenueCurrencyTotals"];

export type RevenueSortKey = "settle_time" | "gross" | "fees" | "net" | "order_no";
export type SortDir = "asc" | "desc";

export interface RevenueFilterState {
  dateFrom?: string; // YYYY-MM-DD
  dateTo?: string;
  marketplace?: string;
  seller?: string;
  currency?: string;
  search: string;
  sortKey: RevenueSortKey;
  sortDir: SortDir;
  offset: number;
  limit: number;
}

export const DEFAULT_LIMIT = 50;

/** YYYY-MM-DD for `d`. */
function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Default filter: last 7 days, newest settle_time first.
 *  dateTo is TODAY (inclusive in the UI, matching the Inbound report's toolbar convention).
 *  filterToQuery() adds +1 day when sending to the API, whose date_to is exclusive.
 */
export function defaultRevenueFilter(now: Date = new Date()): RevenueFilterState {
  const from = new Date(now);
  from.setDate(from.getDate() - 6);
  return {
    dateFrom: isoDate(from),
    dateTo: isoDate(now),
    search: "",
    sortKey: "settle_time",
    sortDir: "desc",
    offset: 0,
    limit: DEFAULT_LIMIT,
  };
}

/** Maps filter state to the API query object (snake_case, omit empties). */
export function filterToQuery(f: RevenueFilterState): {
  date_from?: string;
  date_to?: string;
  marketplace?: string;
  seller?: string;
  currency?: string;
  search?: string;
  sort_key?: RevenueSortKey;
  sort_dir?: SortDir;
  offset?: number;
  limit?: number;
} {
  const q: {
    date_from?: string;
    date_to?: string;
    marketplace?: string;
    seller?: string;
    currency?: string;
    search?: string;
    sort_key?: RevenueSortKey;
    sort_dir?: SortDir;
    offset?: number;
    limit?: number;
  } = {
    sort_key: f.sortKey,
    sort_dir: f.sortDir,
    offset: f.offset,
    limit: f.limit,
  };
  if (f.dateFrom) q.date_from = f.dateFrom;
  // UI "To" is inclusive (matches Inbound report toolbar); the API's date_to is exclusive — add 1 day here.
  if (f.dateTo) {
    const d = new Date(`${f.dateTo}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    q.date_to = isoDate(d);
  }
  if (f.marketplace) q.marketplace = f.marketplace;
  if (f.seller) q.seller = f.seller;
  if (f.currency) q.currency = f.currency;
  if (f.search) q.search = f.search;
  return q;
}

/** Toggles sort: same column flips dir, new column starts desc. */
export function toggleSort(f: RevenueFilterState, key: RevenueSortKey): RevenueFilterState {
  if (f.sortKey === key) {
    return { ...f, sortDir: f.sortDir === "asc" ? "desc" : "asc", offset: 0 };
  }
  return { ...f, sortKey: key, sortDir: "desc", offset: 0 };
}
